import React, { useState, useEffect, useRef } from 'react';
import AdvertisingConsentButton from './AdvertisingConsentButton';
import { createPortal } from 'react-dom';
import { detectEUUser } from '../utils/euDetection';
import { setUserProperties, disableGA, clearAnalyticsCookies, trackAuthCtaClicked } from '../analytics';
import { useUserProfile } from '../contexts/UserProfileContext';
import { useGlobalModal } from '../contexts/ModalContext';
import { syncUserProfileDebounced, updateUserProfile } from '../api/users';
import { submitNewsletterSignup, unsubscribeNewsletter } from '../api/newsletter';
import { updateMondialiRegistrationUsername } from '../api/mondiali';
import {
  getConsentPreferences,
  setConsentPreferences,
} from '../utils/consentPreferences';
import {
  clearFunctionalConsentData,
  safeLocalStorageGetItem,
  safeLocalStorageSetItem,
  safeLocalStorageRemoveItem,
} from '../utils/safeStorage';
import styles from '../styles/UserSettings.module.css';
import profileAvatar from '../assets/profile-avatar.png';
import { useUser, useAuth, useClerk, SignOutButton } from '@clerk/clerk-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useHasEverAuthenticated from '../hooks/useHasEverAuthenticated';
import { useGettoncini } from '../contexts/GettonciniContext';
import { useAlertQueueActions } from '../contexts/AlertQueueContext';
import { resolveNewsletterSignupAlert } from '../utils/newsletterGettoncini';
import { queueResourceAlertsFromPayload } from '../utils/resourceAlerts';
import {
  MONDIALI_AUTH_ERROR_MESSAGE,
  MONDIALI_USERNAME_CONFLICT_MESSAGE,
  getFriendlyMondialiRequestError,
  getMondialiUsernameValidationError,
  isMondialiUsernameConflictError,
  normalizeMondialiUsername,
} from '../utils/mondialiRegistration';
import NewsletterSignupContent from './NewsletterSignupContent';
import FaIcon from './FaIcon';
import ConsentCategorySettings from './ConsentCategorySettings';
import GettonciniWalletItem from './GettonciniWalletItem';
import RewardedGettonciniCard from './RewardedGettonciniCard';
import 'flag-icons/css/flag-icons.min.css';

const MONDIALI_EVENT_TITLE = 'ALMA Giochini Mondiali 2026';
const EMPTY_EVENTS_MESSAGE = 'Non hai ancora partecipato a nessun evento.';

const DIFFICULTY_OPTIONS = [
  {
    'name': 'A1',
    'value': 'A1',
    'title': 'A1 - elementare'
  },
  {
    'name': 'A2',
    'value': 'A2',
    'title': 'A2 - pre-intermedio'
  },
  {
    'name': 'B1',
    'value': 'B1',
    'title': 'B1 - intermedio'
  },
  {
    'name': 'B2',
    'value': 'B2',
    'title': 'B2 - intermedio-avanzato'
  },
  {
    'name': 'C1',
    'value': 'C1',
    'title': 'C1 - avanzato'
  },
  {
    'name': 'C2',
    'value': 'C2',
    'title': 'C2 - padronanza'
  },
];

const UserSettings = () => {
  const [isEUUser, setIsEUUser] = useState(null);
  const [functionalConsent, setFunctionalConsent] = useState(false);
  const [analyticsConsent, setAnalyticsConsent] = useState(false);
  const [languageLevel, setLanguageLevel] = useState(null);
  const [email, setEmail] = useState('');
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [transactionalEmailConsent, setTransactionalEmailConsent] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNewsletterSubscribed, setIsNewsletterSubscribed] = useState(false);
  const [isNewsletterLoading, setIsNewsletterLoading] = useState(false);
  const [isTransactionalEmailLoading, setIsTransactionalEmailLoading] = useState(false);
  const [necessaryConsentMessage, setNecessaryConsentMessage] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isMondialiUsernameEditorOpen, setIsMondialiUsernameEditorOpen] = useState(false);
  const [mondialiUsernameDraft, setMondialiUsernameDraft] = useState('');
  const [mondialiUsernameError, setMondialiUsernameError] = useState('');
  const [mondialiUsernameStatus, setMondialiUsernameStatus] = useState('');
  const [isMondialiUsernameSubmitting, setIsMondialiUsernameSubmitting] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const newsletterToggleInProgressRef = useRef(false);
  const mondialiSuccessTimeoutRef = useRef(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const DROPDOWN_WIDTH = 180;

  const { isSignedIn, user } = useUser();
  const { getToken } = useAuth();
  const { openUserProfile } = useClerk();
  const { updateBalance, transactionTypes } = useGettoncini();
  const { queueAlert } = useAlertQueueActions();
  const hasEverAuthenticated = useHasEverAuthenticated();
  const location = useLocation();
  const navigate = useNavigate();
  const { openModal, closeModal } = useGlobalModal();
  
  // Get user profile data from context
  const { 
    languageLevel: profileLanguageLevel,
    email: profileEmail,
    isNewsletterSubscribed: profileIsNewsletterSubscribed,
    isLoading: profileIsLoading,
    profile,
    rememberedName,
    refreshProfile,
    updateProfileLocally,
  } = useUserProfile();

  // Build auth URLs for sign-in and sign-up with current location as next
  const currentPath = location.pathname + location.search;
  const authNextParam = currentPath && currentPath !== '/' 
    ? `next=${encodeURIComponent(currentPath)}`
    : '';
  
  const signupMessage = 'Crea un account gratuito per salvare i tuoi progressi.';
  const signinMessage = 'Accedi al tuo account per sincronizzare i tuoi progressi.';
  const authSource = 'account_settings';
  const authGateReason = 'account_access';
  
  const signupUrl = `/signup?${authNextParam}${authNextParam ? '&' : ''}message=${encodeURIComponent(signupMessage)}&source=${encodeURIComponent(authSource)}&gate_reason=${encodeURIComponent(authGateReason)}`;
  const signinUrl = `/signin?${authNextParam}${authNextParam ? '&' : ''}message=${encodeURIComponent(signinMessage)}&source=${encodeURIComponent(authSource)}&gate_reason=${encodeURIComponent(authGateReason)}`;

  useEffect(() => {
    const initializeSettings = async () => {
      try {
        // Detect if user is in EU
        const euUser = await detectEUUser();
        setIsEUUser(euUser);
        
        // For signed-in users, prefer context values (authoritative from server)
        // Fall back to localStorage for anonymous users or when context not yet loaded
        
        // Get current analytics consent
        const consentPreferences = getConsentPreferences();
        const currentFunctionalConsent = consentPreferences?.categories?.functional === true;
        const currentAnalyticsConsent = consentPreferences?.categories?.analytics === true;
        setFunctionalConsent(currentFunctionalConsent);
        setAnalyticsConsent(currentAnalyticsConsent);
        
        // Get current language level
        if (isSignedIn && profileLanguageLevel) {
          setLanguageLevel(profileLanguageLevel);
        } else {
          const level = safeLocalStorageGetItem('language-level');
          setLanguageLevel(level || null);
        }
        
        // Get email: prefer profile email > Clerk email > localStorage
        let currentEmail = '';
        if (isSignedIn && profileEmail) {
          currentEmail = profileEmail;
        } else if (isSignedIn && user?.primaryEmailAddress?.emailAddress) {
          currentEmail = user.primaryEmailAddress.emailAddress;
        } else {
          currentEmail = localStorage.getItem('email') || '';
        }
        setEmail(currentEmail);
        
        // Get marketing consent from GDPR preferences
        const currentMarketingConsent = consentPreferences?.categories?.marketing === true;
        setMarketingConsent(currentMarketingConsent);
        setTransactionalEmailConsent(profile?.transactional_email_consent === true);
        
        // Get newsletter subscription status
        // Only use context value when profile has actually loaded (not just false from null profile)
        // Skip syncing if a toggle operation is in progress to avoid race conditions
        if (!newsletterToggleInProgressRef.current) {
          if (!profileIsLoading && profile) {
            setIsNewsletterSubscribed(profileIsNewsletterSubscribed);
          } else {
            const savedNewsletterStatus = localStorage.getItem('newsletter-signup-completed');
            setIsNewsletterSubscribed(!!savedNewsletterStatus);
          }
        }
        
      } catch (error) {
        console.error('Error initializing settings:', error);
        // Default to EU behavior on error (safer for GDPR)
        setIsEUUser(true);
        const consentPreferences = getConsentPreferences();
        setFunctionalConsent(consentPreferences?.categories?.functional === true);
        setAnalyticsConsent(consentPreferences?.categories?.analytics === true);
        const level = safeLocalStorageGetItem('language-level');
        setLanguageLevel(level || null);
        
        const savedEmail = localStorage.getItem('email') || '';
        setEmail(savedEmail);
        
        setMarketingConsent(consentPreferences?.categories?.marketing === true);
        setTransactionalEmailConsent(profile?.transactional_email_consent === true);
        
        // Newsletter subscription status - only use context when profile is loaded
        // Skip syncing if a toggle operation is in progress to avoid race conditions
        if (!newsletterToggleInProgressRef.current) {
          if (!profileIsLoading && profile) {
            setIsNewsletterSubscribed(profileIsNewsletterSubscribed);
          } else {
            const savedNewsletterStatus = localStorage.getItem('newsletter-signup-completed');
            setIsNewsletterSubscribed(!!savedNewsletterStatus);
          }
        }
      } finally {
        setIsLoading(false);
      }
    };

    initializeSettings();
  }, [isSignedIn, user, profileLanguageLevel, profileEmail, profileIsNewsletterSubscribed, profileIsLoading, profile]);


  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          buttonRef.current && !buttonRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (mondialiSuccessTimeoutRef.current) {
      clearTimeout(mondialiSuccessTimeoutRef.current);
      mondialiSuccessTimeoutRef.current = null;
    }

    if (mondialiUsernameStatus !== 'Nome utente aggiornato.') {
      return undefined;
    }

    mondialiSuccessTimeoutRef.current = setTimeout(() => {
      setMondialiUsernameStatus((currentStatus) => (
        currentStatus === 'Nome utente aggiornato.' ? '' : currentStatus
      ));
      mondialiSuccessTimeoutRef.current = null;
    }, 3000);

    return () => {
      if (mondialiSuccessTimeoutRef.current) {
        clearTimeout(mondialiSuccessTimeoutRef.current);
        mondialiSuccessTimeoutRef.current = null;
      }
    };
  }, [mondialiUsernameStatus]);

  const handleLanguageLevelChange = (newLevel) => {
    setLanguageLevel(newLevel);
    setIsDropdownOpen(false);
    if (newLevel) {
      safeLocalStorageSetItem('language-level', newLevel);
      setUserProperties({ language_level: newLevel });
    } else {
      safeLocalStorageRemoveItem('language-level');
      setUserProperties({ language_level: null });
    }
    syncUserProfileDebounced(); // Sync to database
  };

  const preserveScrollOnSamePath = () => {
    if (typeof window === 'undefined') {
      return () => {};
    }

    const initialPath = window.location.pathname;
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    return () => {
      if (window.location.pathname !== initialPath) {
        return;
      }

      window.requestAnimationFrame(() => {
        if (window.location.pathname === initialPath) {
          window.scrollTo(scrollX, scrollY);
        }
      });
    };
  };

  const applyConsentCategories = (nextCategories) => {
    const restoreScroll = preserveScrollOnSamePath();
    const currentPreferences = getConsentPreferences();
    setConsentPreferences({
      region: currentPreferences?.region || (isEUUser ? 'eu' : 'non_eu'),
      categories: nextCategories,
      decidedAt: new Date().toISOString(),
      source: 'settings',
    });

    setFunctionalConsent(nextCategories.functional === true);
    setAnalyticsConsent(nextCategories.analytics === true);
    setMarketingConsent(nextCategories.marketing === true);
    syncUserProfileDebounced(); // Sync to database

    if (nextCategories.analytics !== true) {
      disableGA();
      clearAnalyticsCookies();
    }

    if (nextCategories.functional !== true) {
      clearFunctionalConsentData();
    }

    setUserProperties({ email_consent: nextCategories.marketing === true });
    restoreScroll();
  };

  const handleFunctionalConsentChange = (newConsent) => {
    const currentPreferences = getConsentPreferences();
    const nextCategories = {
      necessary: true,
      functional: newConsent,
      analytics: currentPreferences?.categories?.analytics === true,
      marketing: currentPreferences?.categories?.marketing === true,
    };
    applyConsentCategories(nextCategories);
  };

  const handleAnalyticsConsentChange = (newConsent) => {
    const currentPreferences = getConsentPreferences();
    const nextCategories = {
      necessary: true,
      functional: currentPreferences?.categories?.functional === true,
      analytics: newConsent,
      marketing: currentPreferences?.categories?.marketing === true,
    };
    applyConsentCategories(nextCategories);
  };

  const handleMarketingConsentChange = (newConsent) => {
    const currentPreferences = getConsentPreferences();
    const nextCategories = {
      necessary: true,
      functional: currentPreferences?.categories?.functional === true,
      analytics: currentPreferences?.categories?.analytics === true,
      marketing: newConsent,
    };
    applyConsentCategories(nextCategories);
  };

  const handleConsentCategoryToggle = (category, enabled) => {
    if (category === 'functional') {
      handleFunctionalConsentChange(enabled);
      return;
    }
    if (category === 'analytics') {
      handleAnalyticsConsentChange(enabled);
      return;
    }
    if (category === 'marketing') {
      handleMarketingConsentChange(enabled);
    }
  };

  const handleNecessaryConsentInfo = () => {
    setNecessaryConsentMessage('I dati necessari non possono essere disattivati: servono per sicurezza, login e funzioni essenziali.');
  };

  const handleTransactionalEmailConsentChange = async (newConsent) => {
    if (!isSignedIn || isTransactionalEmailLoading) {
      return;
    }

    const previousConsent = profile?.transactional_email_consent === true;
    setIsTransactionalEmailLoading(true);
    setTransactionalEmailConsent(newConsent);
    updateProfileLocally({
      transactional_email_consent: newConsent,
      email_contact_consent: newConsent,
    });

    try {
      const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
      await updateUserProfile({ transactional_email_consent: newConsent }, token);
      await refreshProfile();
    } catch (error) {
      console.error('Transactional email consent update error:', error);
      setTransactionalEmailConsent(previousConsent);
      updateProfileLocally({
        transactional_email_consent: previousConsent,
        email_contact_consent: previousConsent,
      });
    } finally {
      setIsTransactionalEmailLoading(false);
    }
  };

  // Handle newsletter subscription toggle
  const handleNewsletterToggle = async (newValue) => {
    // Get the user's email (Clerk email for signed-in, or stored email from profile/localStorage)
    const userEmail = isSignedIn 
      ? (user?.primaryEmailAddress?.emailAddress || email)
      : email;
    
    // If subscribing and we don't have an email, open the newsletter modal
    // This applies to both anonymous and signed-in users without email
    if (newValue && !userEmail) {
      openModal({
        content: (
          <NewsletterSignupContent
            showDismiss={true}
            onDismiss={closeModal}
            onSuccess={() => {
              closeModal();
              // Update local state after successful signup
              setIsNewsletterSubscribed(true);
              // Refresh to get updated profile
              refreshProfile().catch(() => {});
            }}
          />
        ),
        analytics: {
          source_surface: 'user_settings',
          modal_name: 'newsletter_signup',
          trigger: 'newsletter_toggle_missing_email',
        },
      });
      return;
    }
    
    // If unsubscribing and we don't have an email, just clear local state
    // (shouldn't normally happen, but handle gracefully)
    if (!newValue && !userEmail) {
      localStorage.removeItem('newsletter-signup-completed');
      setIsNewsletterSubscribed(false);
      return;
    }
    
    // Mark that a toggle operation is in progress to prevent race conditions
    // with the useEffect that syncs from profile context
    newsletterToggleInProgressRef.current = true;
    
    // Optimistic update - immediately reflect the new state
    setIsNewsletterSubscribed(newValue);
    setIsNewsletterLoading(true);
    
    try {
      const token = isSignedIn
        ? await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null)
        : null;

      if (newValue) {
        // Subscribe to newsletter
        const result = await submitNewsletterSignup(userEmail, token, 'user_settings_toggle');
        const alertPayload = resolveNewsletterSignupAlert({
          result,
          isSignedIn,
          transactionTypes,
        });
        if (alertPayload) {
          const balance = alertPayload?.payload?.gettoncini?.balance;
          if (typeof balance === 'number') {
            updateBalance(balance);
          }
          queueResourceAlertsFromPayload({
            queueAlert,
            payload: alertPayload.payload,
            isAnonymous: alertPayload.isAnonymous,
            firstAlertDelayMs: 1000,
            signedInDurationMs: 8000,
            anonymousDurationMs: 8000,
          });
        }
        localStorage.setItem('newsletter-signup-completed', Date.now().toString());
        localStorage.setItem('email', userEmail);
      } else {
        // Unsubscribe from newsletter
        await unsubscribeNewsletter(userEmail, token, 'user_settings_toggle');
        localStorage.removeItem('newsletter-signup-completed');
      }
      
      // Refresh profile to sync with server, then clear the in-progress flag
      syncUserProfileDebounced();
      refreshProfile()
        .catch(() => {})
        .finally(() => {
          newsletterToggleInProgressRef.current = false;
        });
    } catch (error) {
      console.error('Newsletter toggle error:', error);
      // Revert the toggle on error
      setIsNewsletterSubscribed(!newValue);
      newsletterToggleInProgressRef.current = false;
    } finally {
      setIsNewsletterLoading(false);
    }
  };

  const getSelectedDisplayText = () => {
    if (!languageLevel) return 'NS';
    const selectedOption = DIFFICULTY_OPTIONS.find(option => option.value === languageLevel);
    return selectedOption ? selectedOption.name : languageLevel;
  };

  const handleDropdownToggle = () => {
    if (!isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 5,
        left: rect.right - DROPDOWN_WIDTH
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Get display name for anonymous users - always show generic name to avoid confusion
  const getAnonymousDisplayName = () => {
    return 'Utente ospite';
  };

  const handleAuthCtaClick = (ctaKind) => {
    trackAuthCtaClicked({
      source_surface: 'account_settings',
      gate_reason: 'account_access',
      cta_kind: ctaKind,
      next_path: currentPath,
      is_returning_device: hasEverAuthenticated,
    });
  };

  // Welcome back message for returning users
  const welcome_back = rememberedName 
    ? `Bentornato/a, ${rememberedName.firstName}!`
    : 'Bentornato/a!';

  const isMondialiRegistered = Boolean(profile?.mondiali?.is_registered);
  const mondialiUsername = profile?.mondiali?.username || '';
  const mondialiCountryAlpha2 = typeof profile?.mondiali?.country_alpha2 === 'string'
    ? profile.mondiali.country_alpha2.trim().toLowerCase()
    : '';
  const hasMondialiCountryFlag = /^[a-z]{2}$/.test(mondialiCountryAlpha2);
  const mondialiSettingsHref = '/mondiali/home';
  const mondialiDescription = `Nome utente: ${mondialiUsername || '-'}`;
  const hasMondialiExpandedContent = Boolean(mondialiUsernameStatus) || isMondialiUsernameEditorOpen;
  const normalizedMondialiUsernameDraft = normalizeMondialiUsername(mondialiUsernameDraft);
  const hasMondialiUsernameChanged = normalizedMondialiUsernameDraft !== mondialiUsername;
  const canSaveMondialiUsername = (
    isSignedIn
    && isMondialiRegistered
    && hasMondialiUsernameChanged
    && !mondialiUsernameError
    && normalizedMondialiUsernameDraft.length > 0
    && !isMondialiUsernameSubmitting
  );

  const openMondialiUsernameEditor = () => {
    setMondialiUsernameDraft(mondialiUsername);
    setMondialiUsernameError('');
    setMondialiUsernameStatus('');
    setIsMondialiUsernameEditorOpen(true);
  };

  const closeMondialiUsernameEditor = () => {
    setMondialiUsernameDraft(mondialiUsername);
    setMondialiUsernameError('');
    setIsMondialiUsernameEditorOpen(false);
  };

  const handleMondialiUsernameChange = (event) => {
    const nextUsername = event.target.value.toLowerCase();
    setMondialiUsernameDraft(nextUsername);
    setMondialiUsernameError(getMondialiUsernameValidationError(nextUsername));
    setMondialiUsernameStatus('');
  };

  const handleMondialiUsernameSubmit = async (event) => {
    event.preventDefault();
    if (!isSignedIn || !isMondialiRegistered || isMondialiUsernameSubmitting) {
      return;
    }

    const nextUsernameError = getMondialiUsernameValidationError(mondialiUsernameDraft);
    setMondialiUsernameError(nextUsernameError);
    setMondialiUsernameStatus('');
    if (nextUsernameError || !hasMondialiUsernameChanged) {
      return;
    }

    setIsMondialiUsernameSubmitting(true);

    try {
      const token = await getToken({ template: 'basic_user_info_jwt_template' }).catch(() => null);
      if (!token) {
        setMondialiUsernameStatus(MONDIALI_AUTH_ERROR_MESSAGE);
        return;
      }

      await updateMondialiRegistrationUsername(
        { username: normalizedMondialiUsernameDraft },
        token,
      );

      updateProfileLocally((previousProfile) => ({
        mondiali: {
          ...(previousProfile?.mondiali || {}),
          is_registered: true,
          username: normalizedMondialiUsernameDraft,
          country_alpha2: previousProfile?.mondiali?.country_alpha2 || null,
        },
      }));
      refreshProfile({ background: true }).catch(() => {});
      setMondialiUsernameStatus('Nome utente aggiornato.');
      setMondialiUsernameError('');
      setIsMondialiUsernameEditorOpen(false);
    } catch (error) {
      if (isMondialiUsernameConflictError(error)) {
        setMondialiUsernameError(MONDIALI_USERNAME_CONFLICT_MESSAGE);
        return;
      }

      setMondialiUsernameStatus(getFriendlyMondialiRequestError(error, {
        fallbackMessage: 'Non siamo riusciti ad aggiornare il nome utente. Riprova tra poco.',
        notFoundMessage: 'Non troviamo la tua registrazione ai Mondiali. Ricarica la pagina e riprova.',
      }));
    } finally {
      setIsMondialiUsernameSubmitting(false);
    }
  };

  const handleMondialiRowClick = () => {
    navigate(mondialiSettingsHref);
  };

  const handleMondialiRowKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleMondialiRowClick();
    }
  };

  if (isLoading) {
    return (
      <div className={styles.settingsContainer}>
        <div className={styles.loadingMessage}>
          Caricamento impostazioni...
        </div>
      </div>
    );
  }

  return (
    <div className={styles.settingsContainer}>
      {/* Profile Section */}
      <div className={styles.profileSection}>
        <div className={styles.avatarContainer}>
          <img 
            src={isSignedIn ? (user?.imageUrl || profileAvatar) : profileAvatar} 
            alt="Profile" 
            className={styles.avatar} 
          />
          {isSignedIn && (
            <div className={styles.statusBadge} title="Account verificato">
              <FaIcon className="fas fa-check"  />
            </div>
          )}
        </div>
        
        <div className={styles.profileInfo}>
          <h2 className={styles.userName}>
            {isSignedIn 
              ? (user?.fullName || user?.firstName || 'Utente')
              : getAnonymousDisplayName()
            }
          </h2>
          {isSignedIn && (
            <p className={styles.userEmail}>
              {user?.primaryEmailAddress?.emailAddress || email || 'Email non impostata'}
            </p>
          )}
          
          {/* Account status indicator */}
          <div className={styles.accountStatus}>
            {isSignedIn ? (
              <span className={styles.statusAuthenticated}>
                <FaIcon className="fas fa-shield-alt"  />
                Account verificato
              </span>
            ) : (
              <span className={styles.statusGuest}>
                <FaIcon className="fas fa-user-clock"  />
                Sessione ospite
              </span>
            )}
          </div>
        </div>
      </div>

      <div className={styles.accountSection}>
        <h3 className={styles.sectionTitle}>Portafoglio</h3>
        <GettonciniWalletItem />
        {isSignedIn ? (
          <div className={styles.walletRewardedCard}>
            <p className={styles.walletRewardedCopy}>
              Guarda un breve annuncio per ottenere gettoncini extra.
            </p>
            <div className={styles.walletRewardedCta}>
              <RewardedGettonciniCard
                sourceSurface="account_page"
                authNextPath="/account"
                signedOutGateReason="account_rewarded_ads_requires_auth"
                signedOutMessage="Accedi per attivare gli annunci premio dal tuo account."
              />
            </div>
          </div>
        ) : null}
      </div>

      <div className={styles.accountSection}>
        <h3 className={styles.sectionTitle}>Eventi</h3>
        {isMondialiRegistered ? (
          <div className={styles.mondialiSettingsItem}>
            <div
              className={`${styles.accountMenuItem} ${hasMondialiExpandedContent ? styles.mondialiAccountMenuItemExpanded : ''}`}
              role="link"
              tabIndex={0}
              onClick={handleMondialiRowClick}
              onKeyDown={handleMondialiRowKeyDown}
            >
              <div className={styles.menuItemIcon}>
                {hasMondialiCountryFlag ? (
                  <span
                    className={`fi fi-${mondialiCountryAlpha2} ${styles.flagIcon}`}
                    aria-hidden="true"
                  >
                  </span>
                ) : (
                  <FaIcon className="fas fa-trophy" />
                )}
              </div>
              <div className={styles.menuItemContent}>
                <span className={styles.menuItemLabel}>{MONDIALI_EVENT_TITLE}</span>
                <span className={styles.mondialiDescriptionRow}>
                  <span className={styles.menuItemDescription}>{mondialiDescription}</span>
                  <button
                    type="button"
                    className={styles.mondialiInlineEditButton}
                    onClick={(event) => {
                      event.stopPropagation();
                      if (isMondialiUsernameEditorOpen) {
                        closeMondialiUsernameEditor();
                        return;
                      }
                      openMondialiUsernameEditor();
                    }}
                    aria-label={isMondialiUsernameEditorOpen ? 'Chiudi modifica nome utente' : 'Modifica nome utente'}
                    title={isMondialiUsernameEditorOpen ? 'Chiudi modifica nome utente' : 'Modifica nome utente'}
                  >
                    <FaIcon className="fas fa-pen" />
                  </button>
                </span>
              </div>
              <FaIcon className="fas fa-chevron-right" />
            </div>

            {hasMondialiExpandedContent && (
              <div className={styles.mondialiExpandedSection}>
                {mondialiUsernameStatus && (
                  <p
                    className={`${styles.mondialiInlineStatus} ${mondialiUsernameStatus === 'Nome utente aggiornato.' ? styles.mondialiInlineStatusSuccess : styles.mondialiInlineStatusError}`}
                    role={mondialiUsernameStatus === 'Nome utente aggiornato.' ? 'status' : 'alert'}
                  >
                    {mondialiUsernameStatus}
                  </p>
                )}

                {isMondialiUsernameEditorOpen && (
                  <form className={styles.mondialiEditor} onSubmit={handleMondialiUsernameSubmit} noValidate>
                    <div className={styles.mondialiField}>
                      <label className={styles.mondialiFieldLabel} htmlFor="mondiali-settings-username">Nome utente</label>
                      <input
                        id="mondiali-settings-username"
                        type="text"
                        value={mondialiUsernameDraft}
                        onChange={handleMondialiUsernameChange}
                        className={`${styles.mondialiInput} ${mondialiUsernameError ? styles.mondialiInputError : ''}`}
                        autoComplete="off"
                        maxLength={16}
                      />
                      {mondialiUsernameError && (
                        <p className={styles.mondialiFieldError} role="alert">
                          {mondialiUsernameError}
                        </p>
                      )}
                    </div>

                    <div className={styles.mondialiEditorActions}>
                      <button
                        type="button"
                        className={styles.mondialiGhostButton}
                        onClick={closeMondialiUsernameEditor}
                        disabled={isMondialiUsernameSubmitting}
                      >
                        Annulla
                      </button>
                      <button
                        type="submit"
                        className={styles.mondialiPrimaryButton}
                        disabled={!canSaveMondialiUsername}
                      >
                        {isMondialiUsernameSubmitting ? 'Salvataggio...' : 'Salva'}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            )}
          </div>
        ) : (
          <p className={styles.emptyEventsMessage}>{EMPTY_EVENTS_MESSAGE}</p>
        )}
      </div>

      {/* Auth CTAs for anonymous users */}
      {!isSignedIn && (
        <>
          {hasEverAuthenticated ? (
            /* Returning user on this device - only show sign in */
            <div className={styles.signinCtaSection}>
              <div className={styles.signinCtaContent}>
                <div className={styles.signinCtaIcon}>
                  <FaIcon className="fas fa-right-to-bracket"  />
                </div>
                <div className={styles.signinCtaText}>
                  <h3>{welcome_back}</h3>
                  <p>Accedi per sincronizzare i tuoi progressi.</p>
                </div>
              </div>
              <Link to={signinUrl} className={styles.signinCtaButton} onClick={() => handleAuthCtaClick('sign_in')}>
                Accedi al tuo account
              </Link>
            </div>
          ) : (
            /* New device - show both options */
            <>
              {/* Sign in block - for returning users */}
              <div className={styles.signinCtaSection}>
                <div className={styles.signinCtaContent}>
                  <div className={styles.signinCtaIcon}>
                    <FaIcon className="fas fa-right-to-bracket"  />
                  </div>
                  <div className={styles.signinCtaText}>
                    <h3>Hai già un account?</h3>
                    <p>Accedi per sincronizzare i tuoi progressi su questo dispositivo.</p>
                  </div>
                </div>
                <Link to={signinUrl} className={styles.signinCtaButton} onClick={() => handleAuthCtaClick('sign_in')}>
                  Accedi al tuo account
                </Link>
              </div>

              {/* Sign up block - for new users */}
              <div className={styles.signupCtaSection}>
                <div className={styles.signupCtaContent}>
                  <div className={styles.signupCtaIcon}>
                    <FaIcon className="fas fa-cloud-upload-alt"  />
                  </div>
                  <div className={styles.signupCtaText}>
                    <h3>Salva i tuoi progressi</h3>
                    <p>Crea un account gratuito per non perdere i tuoi progressi e guadagnare gettoncini.</p>
                  </div>
                </div>
                <Link to={signupUrl} className={styles.signupCtaButton} onClick={() => handleAuthCtaClick('sign_up')}>
                  Crea un account gratuito
                </Link>
              </div>
            </>
          )}
        </>
      )}

      {/* Account Management Section - Only for signed-in users */}
      {isSignedIn && (
        <div className={styles.accountSection}>
          <h3 className={styles.sectionTitle}>Gestione Account</h3>
          
          <button 
            type="button" 
            className={styles.accountMenuItem}
            onClick={() => openUserProfile()}
          >
            <div className={styles.menuItemIcon}>
              <FaIcon className="fas fa-user-edit"  />
            </div>
            <div className={styles.menuItemContent}>
              <span className={styles.menuItemLabel}>Profilo</span>
              <span className={styles.menuItemDescription}>Nome, foto, email</span>
            </div>
            <FaIcon className="fas fa-chevron-right"  />
          </button>
          
          <button 
            type="button" 
            className={styles.accountMenuItem}
            onClick={() => openUserProfile({ customPages: ['/account/security'] })}
          >
            <div className={styles.menuItemIcon}>
              <FaIcon className="fas fa-shield-alt"  />
            </div>
            <div className={styles.menuItemContent}>
              <span className={styles.menuItemLabel}>Sicurezza</span>
              <span className={styles.menuItemDescription}>Password, dispositivi, sessioni</span>
            </div>
            <FaIcon className="fas fa-chevron-right"  />
          </button>

          <SignOutButton redirectUrl="/">
            <button type="button" className={`${styles.accountMenuItem} ${styles.signOutMenuItem}`}>
              <div className={styles.menuItemIcon}>
                <FaIcon className="fas fa-sign-out-alt"  />
              </div>
              <div className={styles.menuItemContent}>
                <span className={styles.menuItemLabel}>Esci</span>
                <span className={styles.menuItemDescription}>Disconnettiti da questo dispositivo</span>
              </div>
            </button>
          </SignOutButton>
        </div>
      )}

      {/* Preferences Section */}
      <div className={styles.preferencesSection}>
        <h3 className={styles.sectionTitle}>Impostazioni</h3>

        <div className={styles.settingsSubsection}>
          <h4 className={styles.subsectionTitle}>
            Gioco
          </h4>
          <div className={styles.settingsRow}>
            <div className={styles.menuItemIcon}>
              <FaIcon className="fas fa-language"  />
            </div>
            <div className={styles.settingsRowInfo}>
              <span className={styles.settingsLabel}>Livello di italiano</span>
              <p className={styles.settingsDescription}>
                Personalizza la difficoltà dei giochi
              </p>
            </div>

            <div className={styles.customSelect}>
              <button
                ref={buttonRef}
                className={`${styles.selectButton} ${isDropdownOpen ? styles.open : ''}`}
                onClick={handleDropdownToggle}
                type="button"
              >
                <span className={styles.selectDisplay}>{getSelectedDisplayText()}</span>
                <span className={styles.selectArrow}>▼</span>
              </button>
            </div>
          </div>
        </div>

        <div className={styles.settingsSubsection}>
          <h4 className={styles.subsectionTitle}>
            Comunicazioni
          </h4>
          {isSignedIn && (
            <div className={styles.settingsRow}>
              <div className={styles.menuItemIcon}>
                <FaIcon className="fas fa-envelope" />
              </div>
              <div className={styles.settingsRowInfo}>
                <span className={styles.settingsLabel}>Email transazionali</span>
                <p className={styles.settingsDescription}>
                  Ricevi email importanti sul tuo account e sul servizio
                </p>
              </div>

              <label className={`${styles.switch} ${isTransactionalEmailLoading ? styles.loading : ''}`}>
                <input
                  type="checkbox"
                  checked={transactionalEmailConsent}
                  onChange={(e) => handleTransactionalEmailConsentChange(e.target.checked)}
                  disabled={isTransactionalEmailLoading}
                />
                <span className={styles.slider}></span>
              </label>
            </div>
          )}
          <div className={styles.settingsRow}>
            <div className={styles.menuItemIcon}>
              <FaIcon className="fas fa-newspaper"  />
            </div>
            <div className={styles.settingsRowInfo}>
              <span className={styles.settingsLabel}>Newsletter</span>
              <p className={styles.settingsDescription}>
                Ricevi aggiornamenti e prova i giochini extra
              </p>
            </div>

            <label className={`${styles.switch} ${isNewsletterLoading ? styles.loading : ''}`}>
              <input
                type="checkbox"
                checked={isNewsletterSubscribed}
                onChange={(e) => handleNewsletterToggle(e.target.checked)}
                disabled={isNewsletterLoading}
              />
              <span className={styles.slider}></span>
            </label>
          </div>
        </div>

        <div className={styles.settingsSubsection}>
          <h4 className={styles.subsectionTitle}>
            Privacy e dati
          </h4>
          <ConsentCategorySettings
            categories={{
              necessary: true,
              functional: functionalConsent,
              analytics: analyticsConsent,
              marketing: marketingConsent,
            }}
            labels={{
              necessary: 'Necessari',
              necessaryDescription: 'Sempre attivi per sicurezza, login e funzioni essenziali',
              functional: 'Funzionali',
              functionalDescription: 'Salva preferenze opzionali come lingua e impostazioni locali',
              analytics: 'Analitici',
              analyticsDescription: "Misurazione d'uso e session replay per migliorare il prodotto",
              marketing: 'Marketing',
              marketingDescription: 'Consenso per comunicazioni promozionali facoltative',
            }}
            onCategoryToggle={handleConsentCategoryToggle}
            onNecessaryAttempt={handleNecessaryConsentInfo}
            necessaryMessage={necessaryConsentMessage}
            necessaryMessageId="user-settings-necessary-consent-message"
          />
          <AdvertisingConsentButton />
        </div>
      </div>

      {isDropdownOpen && createPortal(
        <div 
          className={styles.dropdownMenu}
          ref={dropdownRef}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`
          }}
        >
          <button
            className={`${styles.dropdownOption} ${!languageLevel ? styles.selected : ''}`}
            onClick={() => handleLanguageLevelChange(null)}
          >
            NS - Non specificato
          </button>
          {DIFFICULTY_OPTIONS.map((option) => (
            <button
              key={option.value}
              className={`${styles.dropdownOption} ${languageLevel === option.value ? styles.selected : ''}`}
              onClick={() => handleLanguageLevelChange(option.value)}
            >
              {option.title}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserSettings; 
